import React from "react";

export const INDICATOR_OPTIONS = [
  { id: "rsi_oversold", label: "RSI oversold (<30)", group: "Momentum", dir: "bullish", elite: false, pro: false },
  { id: "rsi_overbought", label: "RSI overbought (>70)", group: "Momentum", dir: "bearish", elite: false, pro: false },
  { id: "low_rsi", label: "Low RSI (<35)", group: "Momentum", dir: "bullish", elite: false, pro: false },
  { id: "high_rsi", label: "High RSI (>65)", group: "Momentum", dir: "bearish", elite: false, pro: false },
  { id: "macd_bull_cross", label: "MACD bull cross", group: "MACD", dir: "bullish", elite: false, pro: false },
  { id: "macd_bear_cross", label: "MACD bear cross", group: "MACD", dir: "bearish", elite: false, pro: false },
  { id: "macd_convergence", label: "MACD convergence", group: "MACD", dir: "neutral", elite: false, pro: false },
  { id: "ema_bull_stack", label: "EMA 9>20>50", group: "Trend", dir: "bullish", elite: false, pro: false },
  { id: "ema_bear_stack", label: "EMA 9<20<50", group: "Trend", dir: "bearish", elite: false, pro: false },
  { id: "above_ema9", label: "EMA 9 > EMA 20", group: "Trend", dir: "bullish", elite: false, pro: false },
  { id: "price_above_vwap", label: "Price > VWAP", group: "VWAP", dir: "bullish", elite: false, pro: true },
  { id: "price_below_vwap", label: "Price < VWAP", group: "VWAP", dir: "bearish", elite: false, pro: true },
  { id: "vwap_bounce", label: "VWAP bounce", group: "VWAP", dir: "neutral", elite: false, pro: true },
  { id: "bb_upper_touch", label: "Upper Bollinger", group: "Bollinger", dir: "bearish", elite: false, pro: true },
  { id: "bb_lower_touch", label: "Lower Bollinger", group: "Bollinger", dir: "bullish", elite: false, pro: true },
  { id: "high_rvol", label: "High RVOL (≥1.5)", group: "Volume", dir: "neutral", elite: false, pro: true },
  { id: "volume_spike", label: "Volume spike", group: "Volume", dir: "neutral", elite: false, pro: true },
];

const MARKETS = [
  { id: "stocks", label: "Stocks" },
  { id: "etfs", label: "ETFs" },
  { id: "crypto", label: "Crypto Proxy" },
  { id: "futures", label: "Futures" },
  { id: "micro_futures", label: "Micros" },
];

const ASSET_TYPES_BY_MARKET = {
  stocks: [
    { id: "large_cap", label: "Large cap" },
    { id: "small_cap", label: "Small cap" },
    { id: "tech", label: "Tech" },
    { id: "energy", label: "Energy" },
  ],
  etfs: [
    { id: "index_etf", label: "Index" },
    { id: "sector_etf", label: "Sector" },
  ],
  crypto: [{ id: "crypto_proxy", label: "BTC/ETH/Miners ETFs" }],
  futures: [{ id: "futures", label: "All futures" }],
  micro_futures: [{ id: "micro_futures", label: "All micros" }],
};

const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];
const STRENGTHS = [
  { id: "weak", label: "Weak" },
  { id: "medium", label: "Medium" },
  { id: "strong", label: "Strong" },
  { id: "elite", label: "Elite" },
];
const DIRECTIONS = [
  { id: "", label: "Any" },
  { id: "bullish", label: "Bullish", variant: "bull" },
  { id: "bearish", label: "Bearish", variant: "bear" },
];

export default function FilterBuilder({ filters, setFilters, onRun, loading, userPlan, onSaveScan }) {
  const toggleArr = (key, val) => {
    const arr = filters[key] || [];
    if (arr.includes(val)) setFilters({ ...filters, [key]: arr.filter((x) => x !== val) });
    else setFilters({ ...filters, [key]: [...arr, val] });
  };
  const indicatorsByGroup = INDICATOR_OPTIONS.reduce((acc, o) => { (acc[o.group] = acc[o.group] || []).push(o); return acc; }, {});

  return (
    <div className="card-surface p-5 fade-in" data-testid="filter-builder">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-mono">Filter Builder</div>
          <h3 className="text-[17px] text-white font-semibold tracking-tight mt-1">Compose setup</h3>
        </div>
        <button onClick={onRun} disabled={loading} className="btn-accent" data-testid="run-scan-btn">
          {loading ? "Scanning…" : "Run scan"}
        </button>
      </div>

      {onSaveScan && (
        <button onClick={onSaveScan} className="w-full btn-ghost mb-4 text-xs flex items-center justify-center gap-1.5" data-testid="filter-save-scan-btn">
          Save current as preset
        </button>
      )}

      <div className="space-y-5">
        <Section label="Market">
          <Chips options={MARKETS} selected={[filters.market]} onSelect={(id) => setFilters({ ...filters, market: id, asset_types: [] })} testidPrefix="market" />
        </Section>

        <Section label="Asset type">
          <Chips options={ASSET_TYPES_BY_MARKET[filters.market] || []} selected={filters.asset_types} onSelect={(id) => toggleArr("asset_types", id)} multi testidPrefix="asset-type" />
        </Section>

        <Section label="Timeframe">
          <div className="grid grid-cols-7 gap-1.5">
            {TIMEFRAMES.map((t) => (
              <button key={t} onClick={() => setFilters({ ...filters, timeframe: t })} data-testid={`timeframe-${t}`}
                className={`py-2 text-[11px] font-mono uppercase tracking-wider border rounded-lg transition-all ${
                  filters.timeframe === t
                    ? "border-white/30 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
                    : "border-white/10 text-white/50 hover:text-white hover:border-white/20"
                }`}>
                {t}
              </button>
            ))}
          </div>
          <div className="label-mono mt-1.5 text-white/35">Live Finnhub stream during market hours · 1m – 4h aggregated from real ticks.</div>
        </Section>

        <Section label="Direction">
          <div className="flex gap-1.5">
            {DIRECTIONS.map((d) => {
              const active = (filters.direction || "") === d.id;
              return (
                <button key={d.id || "any"} onClick={() => setFilters({ ...filters, direction: d.id || null })} data-testid={`direction-${d.id || "any"}`}
                  className={`flex-1 py-2 text-[12px] font-medium tracking-tight border rounded-lg transition-all ${
                    active
                      ? d.variant === "bull"
                        ? "border-[var(--bull)]/40 bg-[var(--bull)]/10 text-[var(--bull)]"
                        : d.variant === "bear"
                          ? "border-[var(--bear)]/40 bg-[var(--bear)]/10 text-[var(--bear)]"
                          : "border-white/30 bg-white/10 text-white"
                      : "border-white/10 text-white/50 hover:text-white hover:border-white/20"
                  }`}>
                  {d.label}
                </button>
              );
            })}
          </div>
        </Section>

        <Section label="Signal strength">
          <div className="grid grid-cols-4 gap-1.5">
            {STRENGTHS.map((s, i) => {
              const active = filters.signal_strength === s.id;
              return (
                <button key={s.id} onClick={() => setFilters({ ...filters, signal_strength: s.id })} data-testid={`strength-${s.id}`}
                  className={`py-2 text-[11px] font-medium border rounded-lg transition-all ${
                    active ? "border-white/30 bg-white/10 text-white" : "border-white/10 text-white/50 hover:text-white"
                  }`}>
                  <div className="flex flex-col items-center gap-1">
                    <span>{s.label}</span>
                    <div className="flex gap-0.5">
                      {[0,1,2,3].map((j) => (
                        <span key={j} className={`block w-1 h-1 rounded-full ${j <= i ? (active ? "bg-[var(--bull)]" : "bg-white/40") : "bg-white/10"}`} />
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>

        <hr className="hr-soft" />

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="label-mono">Indicators</div>
            <div className="label-mono text-white/40">{filters.indicators.length} selected</div>
          </div>
          <div className="space-y-3">
            {Object.entries(indicatorsByGroup).map(([group, opts]) => (
              <div key={group}>
                <div className="text-[10px] font-mono tracking-[0.16em] uppercase text-white/30 mb-1.5">{group}</div>
                <div className="flex flex-wrap gap-1.5">
                  {opts.map((o) => {
                    const active = filters.indicators.includes(o.id);
                    const locked = (o.elite && userPlan !== "executive") || (o.pro && userPlan === "free");
                    const variant = o.dir === "bullish" ? "bull" : o.dir === "bearish" ? "bear" : "";
                    return (
                      <button key={o.id} data-testid={`indicator-${o.id}`} onClick={() => !locked && toggleArr("indicators", o.id)}
                        className={`chip ${variant} ${active ? "active" : ""} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                        <span>{o.label}</span>
                        {locked && <span className="text-[9px] uppercase tracking-wider text-[var(--bull)] font-semibold">{o.elite ? "Exec" : "Prem"}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      {children}
    </div>
  );
}

function Chips({ options, selected, onSelect, multi, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = selected.includes(o.id);
        return (
          <button key={o.id || "any"} data-testid={`${testidPrefix}-${o.id || "any"}`} onClick={() => onSelect(o.id)}
            className={`chip ${active ? "active" : ""}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
